/**
 * GitignoreManager — auto-add artifact directory to .gitignore.
 *
 * Ensures the configured artifact output directory is listed
 * in the project's .gitignore. Additive-only (never removes entries).
 */

import { existsSync, readFileSync, appendFileSync } from 'node:fs';
import { join, parse, relative } from 'node:path';

/**
 * Find the repository root by walking up from startDir looking for `.git`.
 * Returns null if no git repository is found.
 */
function findGitRoot(startDir: string): string | null {
  let dir = startDir;
  const { root } = parse(dir);
  while (dir !== root) {
    if (existsSync(join(dir, '.git'))) {
      return dir;
    }
    dir = join(dir, '..');
  }
  return null;
}

/**
 * Ensure the artifact output directory is listed in .gitignore.
 *
 * - If not in a git repo, silently returns (no error).
 * - If the pattern is already present, does nothing.
 * - If missing, appends the pattern with a descriptive comment.
 *
 * @param artifactsDir - Absolute path to the artifacts directory
 */
export function ensureGitignore(artifactsDir: string): void {
  try {
    const gitRoot = findGitRoot(artifactsDir);
    if (!gitRoot) return;

    const gitignorePath = join(gitRoot, '.gitignore');
    const relativePath = relative(gitRoot, artifactsDir).replace(/\\/g, '/');
    const pattern = relativePath.endsWith('/') ? relativePath : `${relativePath}/`;

    if (existsSync(gitignorePath)) {
      const content = readFileSync(gitignorePath, 'utf-8');
      // Check if the pattern (or a parent pattern) already covers the directory
      const lines = content.split('\n').map((l) => l.trim());
      if (lines.some((line) => line === pattern || line === relativePath)) {
        return; // Already covered
      }
    }

    // Append the pattern
    const entry = `\n# TestRelic test artifacts\n${pattern}\n`;
    appendFileSync(gitignorePath, entry, 'utf-8');
  } catch {
    // Never crash — gitignore management is best-effort
  }
}
